import { useMemo, useState } from "react";
import type {
  InstrumentDto,
  OperationDto,
  OrderBookDto,
  OrderDto,
  PortfolioDto,
  QuoteDto,
} from "../shared/contracts.ts";
import {
  changeClass,
  currencySymbol,
  enumLabel,
  formatDateTime,
  formatMoney,
  formatNumber,
  formatPercent,
  numberValue,
  operationDate,
} from "./format.ts";
import {
  compareTableValues,
  SortableHeader,
  toggleSort,
  useResizableColumns,
  type SortState,
  type TableColumn,
} from "./table.tsx";
import { buildDepthGraph, formatCompactQuantity } from "./orderbook.ts";

type PortfolioSortKey =
  | "instrument"
  | "quantity"
  | "currentPrice"
  | "averagePrice"
  | "value"
  | "expectedYield"
  | "expectedYieldPercent"
  | "dailyChange"
  | "dailyChangePercent";

const PORTFOLIO_COLUMNS = [
  { defaultDirection: "asc", key: "instrument", label: "Инструмент", minWidth: 140, width: 290 },
  { defaultDirection: "desc", key: "quantity", label: "Доступно", minWidth: 72, width: 81 },
  { defaultDirection: "desc", key: "currentPrice", label: "Цена", minWidth: 72, width: 90 },
  { defaultDirection: "desc", key: "averagePrice", label: "Средняя", minWidth: 72, width: 81 },
  { defaultDirection: "desc", key: "value", label: "Стоимость", minWidth: 84, width: 99 },
  { defaultDirection: "desc", key: "expectedYield", label: "Доход", minWidth: 78, width: 91 },
  { defaultDirection: "desc", key: "expectedYieldPercent", label: "Доход, %", minWidth: 76, width: 80 },
  { defaultDirection: "desc", key: "dailyChange", label: "За день", minWidth: 76, width: 81 },
  { defaultDirection: "desc", key: "dailyChangePercent", label: "За день, %", minWidth: 84, width: 91 },
] as const satisfies readonly TableColumn<PortfolioSortKey>[];

type OrderSortKey = "instrument" | "direction" | "executedLots" | "price" | "status" | "createdAt";

const ORDER_COLUMNS = [
  { defaultDirection: "asc", key: "instrument", label: "Инструмент", minWidth: 100, width: 130 },
  { defaultDirection: "asc", key: "direction", label: "Сторона", minWidth: 72, width: 82 },
  { defaultDirection: "desc", key: "executedLots", label: "Исполнено", minWidth: 84, width: 96 },
  { defaultDirection: "desc", key: "price", label: "Цена", minWidth: 72, width: 90 },
  { defaultDirection: "asc", key: "status", label: "Статус", minWidth: 92, width: 110 },
  { defaultDirection: "desc", key: "createdAt", label: "Время", minWidth: 96, width: 116 },
] as const satisfies readonly TableColumn<OrderSortKey>[];

const PORTFOLIO_GROUP_ORDER = [
  "currency",
  "share",
  "bond",
  "fund",
  "futures",
  "option",
  "sp",
  "other",
] as const;

const PORTFOLIO_GROUP_LABELS: Record<string, string> = {
  bond: "Облигации",
  currency: "Валюта и металлы",
  fund: "Фонды",
  futures: "Фьючерсы",
  option: "Опционы",
  other: "Прочее",
  share: "Акции",
  sp: "Структурные продукты",
};

function portfolioGroupKey(instrumentType: string): string {
  const type = instrumentType.replace(/^INSTRUMENT_TYPE_/i, "").toLowerCase();
  if (type.includes("currenc") || type.includes("metal")) return "currency";
  if (type.includes("share") || type.includes("stock")) return "share";
  if (type.includes("bond")) return "bond";
  if (type.includes("etf") || type.includes("fund")) return "fund";
  if (type.includes("future")) return "futures";
  if (type.includes("option")) return "option";
  if (type === "sp" || type.includes("struct")) return "sp";
  return "other";
}

function portfolioPercent(value?: number): string {
  return formatPercent(value).replace(/^\+/, "");
}

function groupTotal(portfolio: PortfolioDto, key: string, positions: PortfolioDto["positions"]): string | undefined {
  const upstreamKey = key === "fund" ? "etf" : key;
  const upstreamTotal = portfolio.groupTotals[upstreamKey];
  if (upstreamTotal !== undefined) return upstreamTotal;
  if (positions.some(position => position.instrument.currency?.toUpperCase() !== portfolio.currency.toUpperCase())) return undefined;
  const values = positions.map((position) => numberValue(position.value));
  if (values.some((value) => value === undefined)) return undefined;
  return String(values.reduce<number>((total, value) => total + (value ?? 0), 0));
}

function portfolioSortValue(
  position: PortfolioDto["positions"][number],
  key: PortfolioSortKey,
): number | string | undefined {
  if (key === "instrument") return position.instrument.ticker;
  if (key === "expectedYieldPercent" || key === "dailyChangePercent") return position[key];
  return numberValue(position[key]);
}

function orderSortValue(order: OrderDto, key: OrderSortKey): number | string | undefined {
  switch (key) {
    case "instrument": return order.instrument.ticker;
    case "direction": return enumLabel(order.direction);
    case "executedLots": return numberValue(order.executedLots);
    case "price": return numberValue(order.price);
    case "status": return enumLabel(order.status);
    case "createdAt": return order.createdAt;
  }
}

function instrumentLogoUrl(instrument: InstrumentDto): string | undefined {
  if (!instrument.brandLogoName) return undefined;
  const logoName = instrument.brandLogoName.replace(/\.(?:png|svg)$/i, "");
  return `https://invest-brands.cdn-tinkoff.ru/${encodeURIComponent(logoName)}x160.png`;
}

function PortfolioInstrumentIcon({ instrument }: { instrument: InstrumentDto }) {
  const logoUrl = instrumentLogoUrl(instrument);
  const brandColor = /^#[0-9a-f]{6}$/i.test(instrument.brandColor ?? "")
    ? instrument.brandColor
    : undefined;
  const textColor = /^#[0-9a-f]{6}$/i.test(instrument.brandTextColor ?? "")
    ? instrument.brandTextColor
    : undefined;
  return (
    <span className="portfolio-instrument-icon" style={{ backgroundColor: brandColor, color: textColor }}>
      <span>{instrument.ticker.slice(0, 2)}</span>
      {logoUrl && <img alt="" onError={(event) => { event.currentTarget.hidden = true; }} src={logoUrl} />}
    </span>
  );
}

function WidgetHeader({ children }: { children: React.ReactNode }) {
  return <header className="widget-header"><span className="widget-title">{children}</span></header>;
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="empty-state">{children}</div>;
}

function selectOnKeyboard(event: React.KeyboardEvent, select: () => void): void {
  if (event.key !== "Enter" && event.key !== " ") return;
  event.preventDefault();
  select();
}

export function PortfolioWidget({
  onSelect,
  portfolio,
  selectedUid,
}: {
  onSelect: (uid: string) => void;
  portfolio?: PortfolioDto;
  selectedUid?: string;
}) {
  const [sort, setSort] = useState<SortState<PortfolioSortKey>>({ direction: "desc", key: "value" });
  const columns = useResizableColumns(PORTFOLIO_COLUMNS, "terminal.table.portfolio.columns");
  if (!portfolio) return <section className="widget" data-widget="portfolio"><WidgetHeader>Портфель</WidgetHeader><Empty>Нет данных</Empty></section>;
  const groups = Object.groupBy(
    portfolio.positions,
    (position) => portfolioGroupKey(position.instrument.instrumentType),
  );
  const orderedGroups = PORTFOLIO_GROUP_ORDER
    .map((key) => ({
      key,
      positions: (groups[key] ?? []).toSorted((left, right) => compareTableValues(
        portfolioSortValue(left, sort.key),
        portfolioSortValue(right, sort.key),
        sort.direction,
      )),
    }))
    .filter((group) => group.positions.length > 0);

  return (
    <section className="widget portfolio-widget" data-widget="portfolio">
      <header className="widget-header portfolio-header">
        <span className="widget-title">Брокерский счёт</span>
        <span aria-hidden="true" className="portfolio-window-controls"><i>⌃</i><i>⚙</i><i>×</i></span>
      </header>
      <div className="portfolio-summary">
        <div className="portfolio-total"><strong>{formatMoney(portfolio.totalAmount, portfolio.currency)} <i>⌄</i></strong><span>С плечом до —</span></div>
        <div><strong className={changeClass(numberValue(portfolio.expectedYield))}>{formatMoney(portfolio.expectedYield, portfolio.currency)} <i>•</i> {portfolioPercent(portfolio.expectedYieldPercent)}</strong><span>Все время</span></div>
        <div><strong className={changeClass(numberValue(portfolio.dailyYield))}>{formatMoney(portfolio.dailyYield, portfolio.currency)} <i>•</i> {portfolioPercent(portfolio.dailyYieldPercent)}</strong><span>За сегодня</span></div>
        <div><strong>{formatMoney(portfolio.blocked, portfolio.currency)}</strong><span>Заблокировано <i className="portfolio-help">?</i></span></div>
        <div><strong className={changeClass(numberValue(portfolio.varMargin))}>{formatMoney(portfolio.varMargin, portfolio.currency)}</strong><span>Вармаржа <i className="portfolio-help">?</i></span></div>
      </div>
      <div className="table-scroll portfolio-table-scroll">
        <table className="data-table portfolio-table" style={{ minWidth: columns.totalWidth, width: columns.totalWidth }}>
          <colgroup>{PORTFOLIO_COLUMNS.map((column) => <col key={column.key} style={{ width: columns.widths[column.key] }} />)}</colgroup>
          <thead><tr>{PORTFOLIO_COLUMNS.map((column) => (
            <SortableHeader
              column={column}
              currentWidth={columns.widths[column.key]}
              key={column.key}
              onResizeBy={columns.resizeBy}
              onResizeStart={columns.startResize}
              onSort={(key, direction) => setSort((current) => toggleSort(current, key, direction))}
              sort={sort}
            />
          ))}</tr></thead>
          <tbody>
            {orderedGroups.map(({ key, positions }) => [
              <tr className="group-row portfolio-group-row" key={`group-${key}`}>
                <td><span>{PORTFOLIO_GROUP_LABELS[key]} <i>⌃</i></span></td><td /><td /><td />
                <td>{formatMoney(groupTotal(portfolio, key, positions), portfolio.currency)}</td>
                <td /><td /><td /><td />
              </tr>,
              ...positions.map((position) => (
                <tr
                  aria-label={`Выбрать ${position.instrument.ticker}`}
                  className={`selectable-instrument-row ${selectedUid === position.instrument.uid ? "selected" : ""}`}
                  data-instrument-uid={position.instrument.uid}
                  key={position.instrument.uid}
                  onClick={() => onSelect(position.instrument.uid)}
                  onKeyDown={(event) => selectOnKeyboard(event, () => onSelect(position.instrument.uid))}
                  tabIndex={0}
                >
                  <td><span className="portfolio-instrument-cell"><PortfolioInstrumentIcon instrument={position.instrument} /><strong title={position.instrument.name}>{position.instrument.ticker}</strong></span></td>
                  <td>{formatNumber(position.quantity)}</td>
                  <td>{formatMoney(position.currentPrice, position.instrument.currency)}</td>
                  <td>{formatMoney(position.averagePrice, position.instrument.currency)}</td>
                  <td>{formatMoney(position.value, position.instrument.currency)}</td>
                  <td className={changeClass(numberValue(position.expectedYield))}>{formatMoney(position.expectedYield, position.instrument.currency)}</td>
                  <td className={changeClass(position.expectedYieldPercent)}>{portfolioPercent(position.expectedYieldPercent)}</td>
                  <td className={changeClass(numberValue(position.dailyChange))}>{formatMoney(position.dailyChange, position.instrument.currency)}</td>
                  <td className={changeClass(position.dailyChangePercent)}>{portfolioPercent(position.dailyChangePercent)}</td>
                </tr>
              )),
            ])}
          </tbody>
        </table>
      </div>
    </section>
  );
}

interface InstrumentCardProps {
  instrument?: InstrumentDto;
  portfolio?: PortfolioDto;
  quote?: QuoteDto;
}

export function InstrumentCardWidget({ instrument, portfolio, quote }: InstrumentCardProps) {
  const position = portfolio?.positions.find((item) => item.instrument.uid === instrument?.uid);
  return (
    <section className="widget" data-widget="instrument-card">
      <WidgetHeader>Карточка инструмента</WidgetHeader>
      {!instrument ? <Empty>Выберите инструмент</Empty> : (
        <div className="instrument-card-body">
          <div className="instrument-card-heading">
            <span className="instrument-symbol large">{instrument.ticker.slice(0, 2)}</span>
            <div><strong>{instrument.ticker}</strong><span>{instrument.name}</span></div>
          </div>
          <div className="instrument-current">
            <strong>{formatMoney(quote?.price, instrument.currency)}</strong>
            <span className={changeClass(quote?.changePercent)}>{formatPercent(quote?.changePercent)}</span>
          </div>
          <dl className="facts">
            <div><dt>Позиция</dt><dd>{position ? formatNumber(position.quantity) : "—"}</dd></div>
            <div><dt>Страна</dt><dd>{instrument.country ?? "—"}</dd></div>
            <div><dt>Биржа</dt><dd>{instrument.exchange ?? "—"}</dd></div>
            <div><dt>Тип</dt><dd>{enumLabel(instrument.instrumentType)}</dd></div>
            <div><dt>ISIN</dt><dd>{instrument.isin ?? "—"}</dd></div>
            <div><dt>Лот</dt><dd>{instrument.lot}</dd></div>
          </dl>
        </div>
      )}
    </section>
  );
}

export function OrderBookWidget({
  currency,
  orderBook,
  ticker,
}: {
  currency?: string;
  orderBook?: OrderBookDto;
  ticker?: string;
}) {
  const asks = (orderBook?.asks ?? []).slice(0, 20);
  const bids = (orderBook?.bids ?? []).slice(0, 20);
  const graph = orderBook ? buildDepthGraph(orderBook) : undefined;
  const priceSymbol = currencySymbol(currency);
  const levels = Array.from({ length: Math.max(asks.length, bids.length) }, (_, index) => ({
    ask: asks[index],
    bid: bids[index],
  }));
  const maxQuantity = Math.max(
    1,
    ...asks.map((level) => numberValue(level.quantity) ?? 0),
    ...bids.map((level) => numberValue(level.quantity) ?? 0),
  );

  return (
    <section
      className="widget"
      data-instrument-uid={orderBook?.instrumentUid}
      data-orderbook-time={orderBook?.time}
      data-widget="orderbook"
    >
      <header className="widget-header orderbook-widget-header">
        <span className="orderbook-header-title">
          <span aria-hidden="true" className="orderbook-search-icon" />
          <strong>{ticker ?? "—"}</strong>
        </span>
        <span aria-hidden="true" className="orderbook-window-controls">
          <span>⚙</span><span>☷</span><span>Ⅱ</span><span>×</span>
        </span>
      </header>
      {!orderBook ? <Empty>Ожидание стакана…</Empty> : (
        <div className="orderbook">
          <div className="orderbook-depth" data-testid="orderbook-depth">
            <div className="orderbook-depth-labels">
              <strong>{formatCompactQuantity(graph!.bidTotal)}</strong>
              <strong>{formatMoney(String(graph!.referencePrice), currency)}</strong>
              <strong>{formatCompactQuantity(graph!.askTotal)}</strong>
            </div>
            <svg aria-label="Кумулятивная глубина стакана" preserveAspectRatio="none" role="img" viewBox="0 0 1000 130">
              <line className="depth-center-line" x1="500" x2="500" y1="0" y2="130" />
              <path className="depth-area bid" d={graph!.bidAreaPath} />
              <path className="depth-area ask" d={graph!.askAreaPath} />
              <path className="depth-line bid" d={graph!.bidLinePath} />
              <path className="depth-line ask" d={graph!.askLinePath} />
            </svg>
          </div>
          <div className="book-columns">
            <strong>Покупка, {priceSymbol}</strong>
            <span>{formatSpread(graph!.spread, graph!.spreadPercent)}</span>
            <strong>Продажа, {priceSymbol}</strong>
          </div>
          <div className="book-rows table-scroll">
            {levels.map(({ ask, bid }, index) => (
              <BookRow ask={ask} bid={bid} key={`${index}-${bid?.price ?? ""}-${ask?.price ?? ""}`} max={maxQuantity} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function formatSpread(spread?: number, percent?: number): string {
  if (spread === undefined || percent === undefined) return "—";
  const value = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 4 }).format(spread);
  const relative = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 3 }).format(percent);
  return `${value} (${relative}%)`;
}

function BookRow({
  ask,
  bid,
  max,
}: {
  ask?: { price: string; quantity: string };
  bid?: { price: string; quantity: string };
  max: number;
}) {
  const bidWidth = Math.min(100, ((numberValue(bid?.quantity) ?? 0) / max) * 100);
  const askWidth = Math.min(100, ((numberValue(ask?.quantity) ?? 0) / max) * 100);
  return (
    <div className="book-row">
      <span className="book-bid-price">{formatNumber(bid?.price)}</span>
      <span className="book-quantity bid">
        {bid && <i style={{ width: `${bidWidth}%` }} />}
        <b>{formatNumber(bid?.quantity, 0)}</b>
      </span>
      <span className="book-quantity ask">
        {ask && <i style={{ width: `${askWidth}%` }} />}
        <b>{formatNumber(ask?.quantity, 0)}</b>
      </span>
      <span className="book-ask-price">{formatNumber(ask?.price)}</span>
    </div>
  );
}

export function OrdersWidget({
  onSelect,
  orders,
  selectedUid,
}: {
  onSelect: (uid: string) => void;
  orders: OrderDto[];
  selectedUid?: string;
}) {
  const [sort, setSort] = useState<SortState<OrderSortKey>>();
  const columns = useResizableColumns(ORDER_COLUMNS, "terminal.table.orders.columns");
  const sortedOrders = sort
    ? orders.toSorted((left, right) => compareTableValues(
      orderSortValue(left, sort.key),
      orderSortValue(right, sort.key),
      sort.direction,
    ))
    : orders;
  return (
    <section className="widget" data-widget="orders">
      <WidgetHeader>Активные заявки <span className="count-badge">{orders.length}</span></WidgetHeader>
      <div className="table-scroll">
        {orders.length === 0 ? <Empty>Нет активных заявок</Empty> : (
          <table className="data-table orders-table" style={{ minWidth: columns.totalWidth, width: columns.totalWidth }}>
            <colgroup>{ORDER_COLUMNS.map((column) => <col key={column.key} style={{ width: columns.widths[column.key] }} />)}</colgroup>
            <thead><tr>{ORDER_COLUMNS.map((column) => (
              <SortableHeader
                column={column}
                currentWidth={columns.widths[column.key]}
                key={column.key}
                onResizeBy={columns.resizeBy}
                onResizeStart={columns.startResize}
                onSort={(key, direction) => setSort((current) => toggleSort(current, key, direction))}
                sort={sort}
              />
            ))}</tr></thead>
            <tbody>{sortedOrders.map((order) => (
              <tr
                aria-label={`Выбрать ${order.instrument.ticker}`}
                className={`selectable-instrument-row ${selectedUid === order.instrument.uid ? "selected" : ""}`}
                data-instrument-uid={order.instrument.uid}
                key={order.id}
                onClick={() => onSelect(order.instrument.uid)}
                onKeyDown={(event) => selectOnKeyboard(event, () => onSelect(order.instrument.uid))}
                tabIndex={0}
              >
                <td><strong>{order.instrument.ticker}</strong><small>{enumLabel(order.type)}</small></td>
                <td className={order.direction.includes("BUY") ? "positive" : "negative"}>{enumLabel(order.direction)}</td>
                <td>{formatNumber(order.executedLots, 9)} / {formatNumber(order.lots, 9)}</td>
                <td>{formatMoney(order.price, order.instrument.currency)}</td>
                <td>{enumLabel(order.status)}</td>
                <td>{formatDateTime(order.createdAt)}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </section>
  );
}

export function OperationsWidget({
  onSelect,
  operations,
  selectedUid,
}: {
  onSelect: (uid: string) => void;
  operations: OperationDto[];
  selectedUid?: string;
}) {
  const grouped = useMemo(() => Object.groupBy(
    operations.slice(0, 100),
    (operation) => operationDate(operation.date),
  ), [operations]);
  return (
    <section className="widget" data-widget="operations">
      <WidgetHeader>История операций</WidgetHeader>
      <div className="operations-list table-scroll">
        {operations.length === 0 && <Empty>Операций пока нет</Empty>}
        {Object.entries(grouped).map(([date, items]) => items && (
          <div className="operation-day" key={date}>
            <h3>{date}</h3>
            {items.map((operation) => {
              const selectable = Boolean(operation.instrumentUid);
              const selectOperation = () => operation.instrumentUid && onSelect(operation.instrumentUid);
              return (
                <div
                  aria-label={selectable ? `Выбрать инструмент операции ${operation.description}` : undefined}
                  className={`operation-row ${selectable ? "selectable-instrument-row" : ""} ${operation.instrumentUid === selectedUid ? "selected" : ""}`}
                  data-instrument-uid={operation.instrumentUid}
                  key={operation.id}
                  onClick={selectable ? selectOperation : undefined}
                  onKeyDown={selectable ? (event) => selectOnKeyboard(event, selectOperation) : undefined}
                  role={selectable ? "button" : undefined}
                  tabIndex={selectable ? 0 : undefined}
                >
                  <span className="operation-icon">{operation.payment?.startsWith("-") ? "−" : "+"}</span>
                  <span><strong>{operation.description}</strong><small>{formatDateTime(operation.date)}</small></span>
                  <span className={changeClass(numberValue(operation.payment))}><strong>{formatMoney(operation.payment, operation.currency)}</strong><small>{operation.quantity ? `${formatNumber(operation.quantity)} шт.` : enumLabel(operation.state)}</small></span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </section>
  );
}
